import { SearchOutlined, SmileOutlined } from '@ant-design/icons';
import { Button, Empty, Input, Popover, Tooltip } from 'antd';
import { useCallback, useMemo, useState } from 'react';

import {
  EMOJI_CATEGORIES,
  type EmojiCategory,
  type EmojiItem,
} from '@app/components/LeadCard/ui/MessageComposer/data/emojiCategories';

import type { FC } from 'react';

import './LeadEmojiPicker.scss';

const RECENT_EMOJI_STORAGE_KEY = 'lead-card-recent-emojis';
const RECENT_EMOJI_LIMIT = 24;
const DEFAULT_CATEGORY_ID = EMOJI_CATEGORIES[0]?.id ?? 'smileys';

interface LeadEmojiPickerProps {
  disabled?: boolean;
  onEmojiSelect(emoji: string): void;
}

const getEmojiSearchText = (emojiItem: EmojiItem): string =>
  [emojiItem.emoji, emojiItem.name, ...emojiItem.keywords]
    .join(' ')
    .toLowerCase();

const getEmojiKey = (emojiItem: EmojiItem): string =>
  `${emojiItem.emoji}-${emojiItem.name}`;

const getEmojiPickerPopupContainer = (triggerNode: HTMLElement): HTMLElement =>
  triggerNode.closest<HTMLElement>('.LeadCard') ?? document.body;

const getRecentEmojis = (): string[] => {
  try {
    const savedValue = localStorage.getItem(RECENT_EMOJI_STORAGE_KEY);
    if (!savedValue) return [];

    const parsedValue = JSON.parse(savedValue);
    if (!Array.isArray(parsedValue)) return [];

    return parsedValue
      .filter((item): item is string => typeof item === 'string')
      .slice(0, RECENT_EMOJI_LIMIT);
  } catch {
    return [];
  }
};

const saveRecentEmojis = (emojis: string[]): void => {
  try {
    localStorage.setItem(RECENT_EMOJI_STORAGE_KEY, JSON.stringify(emojis));
  } catch {
    // localStorage can be unavailable in private or restricted browser modes
  }
};

export const LeadEmojiPicker: FC<LeadEmojiPickerProps> = ({
  disabled,
  onEmojiSelect,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeCategoryId, setActiveCategoryId] =
    useState<EmojiCategory['id']>(DEFAULT_CATEGORY_ID);
  const [recentEmojis, setRecentEmojis] = useState<string[]>(getRecentEmojis);

  const flatEmojiItems = useMemo(
    () => EMOJI_CATEGORIES.flatMap(({ items }) => items),
    []
  );

  const emojiBySymbol = useMemo(() => {
    const emojiMap = new Map<string, EmojiItem>();

    flatEmojiItems.forEach((emojiItem) => {
      if (!emojiMap.has(emojiItem.emoji)) {
        emojiMap.set(emojiItem.emoji, emojiItem);
      }
    });

    return emojiMap;
  }, [flatEmojiItems]);

  const activeCategory = useMemo(
    () =>
      EMOJI_CATEGORIES.find(({ id }) => id === activeCategoryId) ??
      EMOJI_CATEGORIES[0],
    [activeCategoryId]
  );

  const normalizedSearchQuery = searchQuery.trim().toLowerCase();

  const searchResults = useMemo(() => {
    if (!normalizedSearchQuery) return [];

    return flatEmojiItems.filter((emojiItem) =>
      getEmojiSearchText(emojiItem).includes(normalizedSearchQuery)
    );
  }, [flatEmojiItems, normalizedSearchQuery]);

  const recentEmojiItems = useMemo(
    () =>
      recentEmojis.map(
        (emoji) =>
          emojiBySymbol.get(emoji) ?? {
            emoji,
            name: emoji,
            keywords: [],
          }
      ),
    [emojiBySymbol, recentEmojis]
  );

  const selectEmojiHandler = useCallback(
    (emoji: string) => {
      onEmojiSelect(emoji);

      setRecentEmojis((prevEmojis) => {
        const nextEmojis = [
          emoji,
          ...prevEmojis.filter((recentEmoji) => recentEmoji !== emoji),
        ].slice(0, RECENT_EMOJI_LIMIT);

        saveRecentEmojis(nextEmojis);

        return nextEmojis;
      });
    },
    [onEmojiSelect]
  );

  const renderEmojiGrid = (items: EmojiItem[]) => (
    <div className="LeadEmojiPicker__grid">
      {items.map((emojiItem) => (
        <button
          className="LeadEmojiPicker__item"
          key={getEmojiKey(emojiItem)}
          title={emojiItem.name}
          type="button"
          onClick={() => selectEmojiHandler(emojiItem.emoji)}
        >
          {emojiItem.emoji}
        </button>
      ))}
    </div>
  );

  const content = (
    <div className="LeadEmojiPicker">
      <Input
        allowClear
        className="LeadEmojiPicker__search"
        placeholder="Поиск эмодзи"
        prefix={<SearchOutlined />}
        size="small"
        value={searchQuery}
        onChange={({ target: { value } }) => setSearchQuery(value)}
      />

      <div className="LeadEmojiPicker__categories" role="tablist">
        {EMOJI_CATEGORIES.map((category) => (
          <Tooltip key={category.id} title={category.label}>
            <button
              aria-label={category.label}
              aria-selected={activeCategoryId === category.id}
              className={[
                'LeadEmojiPicker__category',
                activeCategoryId === category.id &&
                  'LeadEmojiPicker__category--active',
              ]
                .filter(Boolean)
                .join(' ')}
              role="tab"
              type="button"
              onClick={() => {
                setActiveCategoryId(category.id);
                setSearchQuery('');
              }}
            >
              {category.iconEmoji}
            </button>
          </Tooltip>
        ))}
      </div>

      <div className="LeadEmojiPicker__content">
        {normalizedSearchQuery ? (
          <section className="LeadEmojiPicker__section">
            <div className="LeadEmojiPicker__section-title">Результаты</div>
            {searchResults.length > 0 ? (
              renderEmojiGrid(searchResults)
            ) : (
              <Empty
                className="LeadEmojiPicker__empty"
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description="Ничего не найдено"
              />
            )}
          </section>
        ) : (
          <>
            {recentEmojiItems.length > 0 && (
              <section className="LeadEmojiPicker__section">
                <div className="LeadEmojiPicker__section-title">Недавние</div>
                {renderEmojiGrid(recentEmojiItems)}
              </section>
            )}
            <section className="LeadEmojiPicker__section">
              <div className="LeadEmojiPicker__section-title">
                {activeCategory.label}
              </div>
              {renderEmojiGrid(activeCategory.items)}
            </section>
          </>
        )}
      </div>
    </div>
  );

  return (
    <Popover
      arrow={false}
      classNames={{
        body: 'LeadEmojiPicker__popover-body',
        root: 'LeadEmojiPicker__popover',
      }}
      content={content}
      getPopupContainer={getEmojiPickerPopupContainer}
      open={isOpen}
      placement="topLeft"
      styles={{
        root: {
          maxWidth: 'calc(100vw - 16px)',
        },
      }}
      trigger="click"
      onOpenChange={setIsOpen}
    >
      <Tooltip title="Реакции">
        <Button
          aria-label="Реакции"
          disabled={disabled}
          icon={<SmileOutlined />}
          size="small"
        />
      </Tooltip>
    </Popover>
  );
};
